/* UsoDiarioPanel.jsx — Panel reutilizable (tabla + filtros + export Excel + edición admin).
   Usado por: /admin/uso-diario  y  pestaña en /admin/control-vehicular.
   Octavio 18-jun-2026 · edición admin 20-jun-2026.
*/
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsoDiarioPanel() {
  const { user } = useAuth();
  const puedeEditar = user?.rol === "admin";
  const [items, setItems] = useState([]);
  const [areas, setAreas] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fFecha, setFFecha] = useState("");
  const [fArea, setFArea] = useState("");
  const [fNoEco, setFNoEco] = useState("");
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  // Edición inline (solo admin)
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ nombre_chofer: "", no_empleado: "", area: "" });
  const [saving, setSaving] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fFecha) params.fecha = fFecha;
      if (fArea) params.area = fArea;
      if (fNoEco.trim()) params.no_economico = fNoEco.trim();
      const r = await axios.get(`${API}/uso-diario`, { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setAreas(r.data.areas_disponibles || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar registros");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [fFecha, fArea, fNoEco]);

  const exportar = async () => {
    setExporting(true);
    try {
      const params = {};
      if (fFecha) params.fecha = fFecha;
      if (fArea) params.area = fArea;
      if (fNoEco.trim()) params.no_economico = fNoEco.trim();
      const r = await axios.get(`${API}/uso-diario/exportar`, { params, responseType: "blob" });
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `uso_diario_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      toast.success("Excel descargado");
    } catch (e) {
      toast.error("Error al exportar");
    } finally {
      setExporting(false);
    }
  };

  // Edición admin (Octavio 20-jun-2026)
  const iniciarEdicion = (r) => {
    setEditId(r.id);
    setEditForm({
      nombre_chofer: r.nombre_chofer || "",
      no_empleado: r.no_empleado || "",
      area: r.area || "",
    });
  };
  const cancelarEdicion = () => {
    setEditId(null);
    setEditForm({ nombre_chofer: "", no_empleado: "", area: "" });
  };
  const guardar = async (id) => {
    if (!editForm.nombre_chofer.trim()) return toast.error("Chofer obligatorio");
    if (!editForm.no_empleado.trim()) return toast.error("No. empleado obligatorio");
    if (!editForm.area) return toast.error("Área obligatoria");
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/uso-diario/${id}`, editForm);
      toast.success("Registro actualizado");
      // Actualiza en sitio
      setItems((prev) => prev.map((it) => it.id === id ? r.data.registro : it));
      cancelarEdicion();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const importarHistorico = async (e) => {
    const file = e.target.files?.[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = "";          // reset para permitir re-subida del mismo archivo
    if (!file) return;
    const token = prompt("Token de administrador:");
    if (!token) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(
        `${API}/uso-diario/importar-historico?token=${encodeURIComponent(token)}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const { insertados, saltados_duplicado, errores } = r.data;
      toast.success(`Importados: ${insertados} · Duplicados saltados: ${saltados_duplicado}${errores?.length ? ` · Errores: ${errores.length}` : ""}`);
      if (errores?.length) console.warn("Errores de importación:", errores);
      cargar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="uso-diario-panel" style={{ background: "white", borderRadius: 12, padding: 16, border: "1px solid #E2E8F0" }}>
      {/* Filtros */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14, alignItems: "center" }}>
        <input
          type="date"
          value={fFecha}
          onChange={(e) => setFFecha(e.target.value)}
          data-testid="uso-flt-fecha"
          style={inp}
        />
        <select value={fArea} onChange={(e) => setFArea(e.target.value)} data-testid="uso-flt-area" style={{ ...inp, minWidth: 220 }}>
          <option value="">Todas las áreas</option>
          {areas.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input
          type="text"
          value={fNoEco}
          onChange={(e) => setFNoEco(e.target.value)}
          placeholder="No. unidad"
          data-testid="uso-flt-no-eco"
          style={{ ...inp, width: 130 }}
        />
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "#64748B" }} data-testid="uso-contador">
            {loading ? "Cargando…" : `${items.length} de ${total} registros`}
          </span>
          {/* Input oculto para seleccionar el Excel histórico */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            data-testid="uso-input-importar"
            onChange={importarHistorico}
          />
          {puedeEditar && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              data-testid="uso-btn-importar"
              title="Importar registros históricos desde Excel"
              style={{
                background: "#1E40AF", color: "white", border: 0,
                padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
                opacity: importing ? 0.6 : 1,
              }}
            >
              <i className="fa-solid fa-file-import mr-1"/>{importing ? "Importando…" : "Cargar Excel histórico"}
            </button>
          )}
          <button
            type="button"
            onClick={exportar}
            disabled={exporting}
            data-testid="uso-btn-exportar"
            style={{
              background: "#15803D", color: "white", border: 0,
              padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}
          >
            <i className="fa-solid fa-file-excel mr-1"/>{exporting ? "Generando…" : "Exportar Excel"}
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div style={{ overflowX: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#F1F5F9" }}>
            <tr style={{ textAlign: "left", color: "#475569", fontSize: 11, textTransform: "uppercase" }}>
              <th style={th}>Fecha</th>
              <th style={th}>No. eco</th>
              <th style={th}>Descripción</th>
              <th style={th}>Chofer</th>
              <th style={th}>No. empleado</th>
              <th style={th}>Área</th>
              <th style={{ ...th, textAlign: "center" }}>Odómetro</th>
              {puedeEditar && <th style={{ ...th, textAlign: "center", width: 80 }}>Acción</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={puedeEditar ? 8 : 7} style={{ textAlign: "center", padding: 24, color: "#94A3B8", fontSize: 13 }}>
                  <i className="fa-solid fa-inbox mr-2"/>Sin registros
                </td>
              </tr>
            )}
            {items.map((r) => {
              const isEditing = editId === r.id;
              return (
                <tr key={r.id} style={{ borderTop: "1px solid #F1F5F9", background: isEditing ? "#FEF9C3" : "transparent" }} data-testid={`uso-row-${r.id}`}>
                  <td style={td}>{r.fecha}</td>
                  <td style={{ ...td, fontFamily: "monospace" }}>{r.no_economico}</td>
                  <td style={td}>{r.descripcion_unidad}</td>
                  <td style={td}>
                    {isEditing ? (
                      <input value={editForm.nombre_chofer}
                             onChange={(e) => setEditForm(p => ({ ...p, nombre_chofer: e.target.value.toUpperCase() }))}
                             data-testid={`uso-edit-chofer-${r.id}`}
                             style={inlineInp}/>
                    ) : r.nombre_chofer}
                  </td>
                  <td style={{ ...td, fontFamily: "monospace" }}>
                    {isEditing ? (
                      <input value={editForm.no_empleado}
                             onChange={(e) => setEditForm(p => ({ ...p, no_empleado: e.target.value }))}
                             data-testid={`uso-edit-noemp-${r.id}`}
                             style={inlineInp}/>
                    ) : r.no_empleado}
                  </td>
                  <td style={td}>
                    {isEditing ? (
                      <select value={editForm.area}
                              onChange={(e) => setEditForm(p => ({ ...p, area: e.target.value }))}
                              data-testid={`uso-edit-area-${r.id}`}
                              style={inlineInp}>
                        {areas.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    ) : r.area}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {r.odometro_funciona === true && (
                      <span style={{ background: "#DCFCE7", color: "#14532D", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>SÍ</span>
                    )}
                    {r.odometro_funciona === false && (
                      <span style={{ background: "#FEE2E2", color: "#7F1D1D", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>NO</span>
                    )}
                    {r.odometro_funciona === undefined && <span style={{ color: "#94A3B8" }}>—</span>}
                  </td>
                  {puedeEditar && (
                    <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>
                      {isEditing ? (
                        <>
                          <button type="button" onClick={() => guardar(r.id)} disabled={saving}
                                  data-testid={`uso-edit-save-${r.id}`}
                                  style={btnSave}>
                            <i className="fa-solid fa-check"/>
                          </button>
                          <button type="button" onClick={cancelarEdicion}
                                  data-testid={`uso-edit-cancel-${r.id}`}
                                  style={btnCancel}>
                            <i className="fa-solid fa-xmark"/>
                          </button>
                        </>
                      ) : (
                        <button type="button" onClick={() => iniciarEdicion(r)}
                                data-testid={`uso-edit-btn-${r.id}`}
                                title="Editar"
                                style={btnEdit}>
                          <i className="fa-solid fa-pen"/>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const inp = {
  border: "1px solid #CBD5E1", borderRadius: 6, padding: "6px 10px", fontSize: 13,
  background: "white",
};
const th = { padding: "10px 12px" };
const td = { padding: "10px 12px", color: "#1E293B" };
const inlineInp = {
  width: "100%", border: "1px solid #94A3B8", borderRadius: 4,
  padding: "4px 6px", fontSize: 12, background: "white", boxSizing: "border-box",
};
const btnEdit = {
  background: "#1E293B", color: "white", border: 0,
  padding: "5px 9px", borderRadius: 5, fontSize: 11, cursor: "pointer",
};
const btnSave = {
  background: "#15803D", color: "white", border: 0,
  padding: "5px 9px", borderRadius: 5, fontSize: 11, cursor: "pointer", marginRight: 4,
};
const btnCancel = {
  background: "#94A3B8", color: "white", border: 0,
  padding: "5px 9px", borderRadius: 5, fontSize: 11, cursor: "pointer",
};
